import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { formatTrNumberValue, formatTrNumberInput, parseTrNumber } from '../../utils/reconciliationUtils';

// Binlik ayraci eklendikce imlec kaymasin diye: imlecten onceki "anlamli"
// karakter (rakam/virgul/eksi) sayisi korunur, gruplama noktalari sayilmaz.
const SIGNIFICANT = /[\d,-]/;

const countSignificant = (text) => (text.match(/[\d,-]/g) || []).length;

const caretIndexFor = (text, significantCount) => {
    if (significantCount <= 0) {
        return 0;
    }

    let seen = 0;
    for (let index = 0; index < text.length; index += 1) {
        if (SIGNIFICANT.test(text[index])) {
            seen += 1;
            if (seen === significantCount) {
                return index + 1;
            }
        }
    }

    return text.length;
};

/**
 * Turkce formatli tutar girisi: yazarken 3186576,50 -> 3.186.576,50 olur.
 * type="number" binlik ayraci gosteremedigi icin text + inputMode="decimal".
 * onCommit(parsedValue) => Promise<boolean>: blur'da kaydeden kullanim; false donerse alan eski
 *   degerine doner. Verilmezse alan sadece biçimlenir (form icinde kullanim).
 * onValueChange(parsedValue): her tusta cagrilir (sayi | null (bos) | undefined (cozulemedi)).
 */
const BalanceInput = ({ value, onCommit, onValueChange, className, ariaLabel, id }) => {
    const inputRef = useRef(null);
    const caretRef = useRef(null);
    const [text, setText] = useState(() => formatTrNumberValue(value));

    // Dis kaynakli degisiklik (ay degisimi, refetch) alani senkronlar
    useEffect(() => {
        setText(formatTrNumberValue(value));
    }, [value]);

    useLayoutEffect(() => {
        if (caretRef.current !== null && inputRef.current) {
            inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
            caretRef.current = null;
        }
    });

    const handleChange = (event) => {
        const element = event.target;
        const caret = element.selectionStart === null ? element.value.length : element.selectionStart;
        const significantBefore = countSignificant(element.value.slice(0, caret));
        const formatted = formatTrNumberInput(element.value);

        caretRef.current = caretIndexFor(formatted, significantBefore);
        setText(formatted);

        if (onValueChange) {
            onValueChange(parseTrNumber(formatted));
        }
    };

    const handleBlur = async () => {
        const parsed = parseTrNumber(text);

        if (!onCommit) {
            if (parsed !== undefined) {
                setText(formatTrNumberValue(parsed));
            }
            return;
        }

        const saved = await onCommit(parsed);
        setText(saved ? formatTrNumberValue(parsed) : formatTrNumberValue(value));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.target.blur();
        }
    };

    return (
        <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="decimal"
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onFocus={(event) => event.target.select()}
            className={className}
            placeholder="—"
            aria-label={ariaLabel}
        />
    );
};

export default BalanceInput;
